import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Footer from './Footer';
import '../styles/Terms.css';
import '../styles/Safety.css';

// Reuse your existing assets (swap as needed)
import safetyHeroImage from '../assets/electrical-hero.png';
import complianceStandardsImage from '../assets/compliance-standards-image.png';
import environmentalImage from '../assets/environmental-image.png';

export default function SafetyAndCompliance() {
  const heroRef = useRef(null);
  const wrapRef = useRef(null);
  const tocRef  = useRef(null);

  // JS-managed sticky fallback (works even with transforms/smooth-scroll libs)
  useEffect(() => {
    const header = document.querySelector('.header'); // your fixed header selector
    const getHeaderH = () => (header?.offsetHeight || 94);

    const syncFixedMetrics = () => {
      if (!tocRef.current || !wrapRef.current) return;
      const wrapRect = wrapRef.current.getBoundingClientRect();
      tocRef.current.style.setProperty('--toc-left', `${wrapRect.left + window.scrollX}px`);
      tocRef.current.style.setProperty('--toc-width', '280px');
      tocRef.current.style.setProperty('--sticky-top', `${getHeaderH() + 16}px`);
    };

    const onScroll = () => {
      if (!heroRef.current || !tocRef.current || !wrapRef.current) return;

      // bottom cap so TOC stops before the last section
      const stickyTop = getHeaderH() + 16;
      const wrapRect = wrapRef.current.getBoundingClientRect();
      const tocH = tocRef.current.offsetHeight;
      const currentBottom = window.scrollY + stickyTop + tocH;
      const boundaryBottom = (document.getElementById('contact-support')?.getBoundingClientRect().bottom ?? wrapRect.bottom) + window.scrollY;
      if (currentBottom > boundaryBottom) {
        tocRef.current.classList.add('is-capped');
        tocRef.current.style.setProperty('--cap-y', `${boundaryBottom - currentBottom}px`);
        syncFixedMetrics();
      } else {
        tocRef.current.classList.remove('is-capped');
        tocRef.current.style.removeProperty('--cap-y');
      }

      // desktop/tablet fixed; phones fall back to normal flow
      if (window.innerWidth < 769) {
        tocRef.current.classList.remove('is-fixed');
        tocRef.current.style.removeProperty('--toc-left');
        tocRef.current.style.removeProperty('--toc-width');
        return;
      }

      const heroBottom = heroRef.current.getBoundingClientRect().bottom;
      if (heroBottom <= getHeaderH() + 16) {
        tocRef.current.classList.add('is-fixed');
        syncFixedMetrics();
      } else {
        tocRef.current.classList.remove('is-fixed');
      }
    };

    const onResize = () => { onScroll(); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    onScroll(); // initial pass
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  const lastUpdated = 'August 12, 2025';

  return (
    <main className="lenis-content terms-page safety-page" aria-labelledby="safety-title">
      <title>About S&D — Nationwide Surplus Buyers</title>
      <meta name="description" content="Who we are, insurance/licensing, safety and compliance standards. Trusted surplus buyers for utilities and industry." />
      <link rel="canonical" href="https://sdindustrialsurplus.com/about-us" />

      {/* Hero */}
      <section ref={heroRef} className="terms-hero safety-hero">
        <div className="terms-hero__content">
          <h1 id="safety-title" className="terms-hero__title">Safety & Compliance</h1>
          <p className="terms-hero__subtitle">
            Practical standards and procedures for safe, compliant handling of industrial transformers, PVF,
            and electrical equipment—from site assessment to final pickup and transport.
          </p>
        </div>
        <div className="terms-hero__overlay" aria-hidden="true" />
        <figure className="safety-hero-media" aria-hidden="true">
        </figure>
      </section>

      {/* Body */}
      <section ref={wrapRef} className="terms-wrap">
        {/* On-this-page */}
        <aside ref={tocRef} className="terms-toc" aria-label="On this page">
          <div className="toc-card">
            <h2 className="toc-title">On this page</h2>
            <nav className="toc-nav">
              <a href="#scope">1. Commitment & Scope</a>
              <a href="#regulatory">2. Regulatory Framework</a>
              <a href="#site-safety">3. Site Safety Protocols</a>
              <a href="#rigging">4. Rigging & Lifting</a>
              <a href="#environmental">5. Environmental Controls</a>
              <a href="#hazmat">6. Oils, PCBs & HazMat</a>
              <a href="#ppe">7. PPE Standards</a>
              <a href="#contractors">8. Contractor & Visitor Requirements</a>
              <a href="#docs">9. Documentation & Chain of Custody</a>
              <a href="#incidents">10. Incident Reporting & Corrective Action</a>
              <a href="#audits">11. Audits & Continuous Improvement</a>
              <a href="#contact-support">12. Contact & Certifications</a>
            </nav>
          </div>
        </aside>

        {/* Content */}
        <article className="terms-body">
          <p className="terms-updated">
            Last updated: {lastUpdated}. <span className="legal-note">This page outlines typical policies and
            procedures. It is not legal advice. Please have counsel review for your operations and jurisdictions.</span>
          </p>

          <section id="scope" className="terms-section">
            <h2>1. Commitment & Scope</h2>
            <p>
              We prioritize the safety of personnel, customers, and the public while meeting or exceeding
              applicable regulations. These guidelines apply to assessments, de‑energization, dismantling,
              loading, and transportation of industrial equipment we purchase or handle on your behalf.
            </p>
          </section>

          <section id="regulatory" className="terms-section">
            <h2>2. Regulatory Framework</h2>
            <div className="safety-columns">
              <ul className="safety-bullets">
                <li><strong>OSHA (29 CFR 1910/1926):</strong> General industry and construction safety, including hazard communication, fall protection, and machine guarding.</li>
                <li><strong>NFPA 70 / NFPA 70E:</strong> Electrical code compliance and arc‑flash risk assessments; LOTO procedures before work begins.</li>
                <li><strong>DOT/FMCSA:</strong> Driver qualifications, load securement, and transport safety for interstate shipments.</li>
                <li><strong>EPA / TSCA / RCRA:</strong> Oils, PCB considerations, spill prevention (SPCC), and waste handling.
                </li>
                <li><strong>ASME B31.3 / ANSI / ASTM:</strong> Applicable standards for process piping, rigging hardware, and PPE performance.
                </li>
              </ul>
            </div>
          </section>

          <section id="site-safety" className="terms-section">
            <h2>3. Site Safety Protocols</h2>
            <ul>
              <li><strong>Pre‑job brief.</strong> Job hazard analysis (JHA), delineation of arc‑flash boundaries, and confirmation of qualified personnel.</li>
              <li><strong>LOTO & de‑energization.</strong> Verification of zero‑energy state; try/test procedures prior to work.</li>
              <li><strong>Area control.</strong> Barricades and spotters. Housekeeping to maintain clear egress and prevent slips and trips.</li>
              <li><strong>Weather & environment.</strong> Heat/cold stress mitigation, wind considerations for lifts, and storm plans.</li>
            </ul>
          </section>

          <section id="rigging" className="terms-section">
            <h2>4. Rigging & Lifting</h2>
            <p>
              Lifts are planned and executed by qualified riggers using rated equipment. Load paths, center‑of‑gravity,
              and sling angles are calculated in advance; lift plans are reviewed when conditions change.
            </p>
            <div className="safety-columns">
              <ul className="safety-bullets">
                <li>Forklifts/cranes sized to load; ground conditions verified.</li>
                <li>Tag lines, pinch‑point awareness, and spotter communication protocols.</li>
                <li>Hardware inspected per ANSI/ASME and removed from service if damaged or uncertifiable.</li>
              </ul>
            </div>
          </section>

          <section id="environmental" className="terms-section">
            <h2>5. Environmental Controls</h2>
            <ul>
              <li>SPCC‑aligned spill kits on site and in vehicles; immediate containment and reporting protocols.</li>
              <li>Non‑reusable materials routed to certified recyclers; manifests retained per recordkeeping rules.</li>
              <li>Local ordinances observed for decommissioning, noise, dust, and transport routing.</li>
            </ul>
          </section>

          <section id="hazmat" className="terms-section">
            <h2>6. Oils, PCBs & Hazardous Materials</h2>
            <p>
              We identify oils and potential PCB content, label where required, and follow DOT hazmat packaging and
              paperwork when applicable. Customers should disclose known PCB‑containing equipment before pickup.
            </p>
          </section>

          <section id="ppe" className="terms-section">
            <h2>7. PPE Standards</h2>
            <ul>
              <li>High‑visibility garments (ANSI/ISEA 107) and task‑appropriate gloves/footwear (ASTM).</li>
              <li>Electrical PPE selected by arc‑flash category per NFPA 70E; inspected and replaced on cycle.</li>
              <li>Respiratory protection and hearing protection deployed as indicated by JHA.</li>
            </ul>
          </section>

          <section id="contractors" className="terms-section">
            <h2>8. Contractor & Visitor Requirements</h2>
            <p>
              Contractors and visitors must follow site rules, use assigned PPE, and remain within designated areas.
              Vendor personnel must be qualified for tasks performed and provide certificates of insurance upon request.
            </p>
          </section>

          <section id="docs" className="terms-section">
            <h2>9. Documentation & Chain of Custody</h2>
            <ul>
              <li>Work permits, lockout tags, lift plans, waste manifests, and shipping papers retained as required.</li>
              <li>Serial numbers recorded for traceability. Chain‑of‑custody documents furnished upon request.</li>
            </ul>
          </section>

          <section id="incidents" className="terms-section">
            <h2>10. Incident Reporting & Corrective Action</h2>
            <p>
              Incidents and near‑misses are reported immediately. Root‑cause analysis (e.g., 5 Whys) informs corrective
              actions. Lessons learned are rolled into training and procedure updates.
            </p>
          </section>

          <section id="audits" className="terms-section">
            <h2>11. Audits & Continuous Improvement</h2>
            <p>
              Internal audits, toolbox talks, and KPI tracking (e.g., TRIR) drive continuous improvement. Findings
              are documented and verified in follow‑up audits.
            </p>
          </section>

          <section id="contact-support" className="terms-section">
            <h2>12. Contact & Certifications</h2>
            <p>
              For questions about our Safety & Compliance program, documentation requests, or certificates,
              please <Link to="/contact-us">contact us</Link>.
            </p>
            <div className="terms-callout" role="note">
              <p><strong>Tip:</strong> Pair this page with your <Link to="/payment-terms-and-conditions">Payment Terms</Link> and <Link to="/shipping-terms-and-conditions">Shipping Terms</Link> for a complete risk and compliance overview.</p>
            </div>
          </section>
        </article>
      </section>

      <Footer />
    </main>
  );
}